import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Settings as SettingsIcon, 
  Loader2,
  Clock,
  Save,
  RefreshCw,
  Zap,
  Calendar,
  Sparkles,
  Key,
  Eye,
  EyeOff,
  Trash2,
  Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton, SkeletonCard } from "../components/ui/skeleton";
import SchedulerSettingsCard from "../components/settings/SchedulerSettingsCard";
import ApiKeysCard from "../components/settings/ApiKeysCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { darkMode } = useOutletContext() || { darkMode: true };
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [intervalHours, setIntervalHours] = useState(72);
  const [isEnabled, setIsEnabled] = useState(true);
  const [scheduleType, setScheduleType] = useState("cron");
  const [cronDayOfWeek, setCronDayOfWeek] = useState("sat");
  const [cronHour, setCronHour] = useState(11);
  const [cronMinute, setCronMinute] = useState(0);
  
  // API Keys state
  const [apiKeysConfig, setApiKeysConfig] = useState(null);
  const [openaiKey, setOpenaiKey] = useState("");
  const [useCustomKey, setUseCustomKey] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const [schedulerRes, apiKeysRes] = await Promise.all([
        axios.get(`${API}/scheduler/config`),
        axios.get(`${API}/settings/api-keys`)
      ]);
      setConfig(schedulerRes.data);
      setIntervalHours(schedulerRes.data.interval_hours || 72);
      setIsEnabled(schedulerRes.data.is_enabled !== false);
      setScheduleType(schedulerRes.data.schedule_type || "cron");
      setCronDayOfWeek(schedulerRes.data.cron_day_of_week || "sat");
      setCronHour(schedulerRes.data.cron_hour ?? 11);
      setCronMinute(schedulerRes.data.cron_minute ?? 0);
      
      setApiKeysConfig(apiKeysRes.data);
      setUseCustomKey(apiKeysRes.data.use_custom_key || false);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/scheduler/config`, {
        is_enabled: isEnabled,
        interval_hours: intervalHours,
        schedule_type: scheduleType,
        cron_day_of_week: cronDayOfWeek,
        cron_hour: cronHour,
        cron_minute: cronMinute
      });
      toast.success("Settings saved successfully!");
      fetchConfig();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const saveApiKeys = async () => {
    setSavingKey(true);
    try {
      await axios.put(`${API}/settings/api-keys`, {
        openai_api_key: openaiKey || null,
        use_custom_key: useCustomKey
      });
      toast.success("API key saved successfully!");
      setOpenaiKey("");
      fetchConfig();
    } catch (error) {
      toast.error("Failed to save API key");
    } finally {
      setSavingKey(false);
    }
  };

  const deleteApiKey = async () => {
    try {
      await axios.delete(`${API}/settings/api-keys`);
      toast.success("Custom API key removed");
      setOpenaiKey("");
      setUseCustomKey(false);
      fetchConfig();
    } catch (error) {
      toast.error("Failed to remove API key");
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-loading">
        {/* Header Skeleton */}
        <div className="animate-fadeIn">
          <Skeleton className={`h-8 w-32 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          <Skeleton className={`h-4 w-64 ${darkMode ? 'bg-[#21262d]' : ''}`} />
        </div>
        {/* Settings Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div 
              key={i} 
              className={`rounded-xl overflow-hidden animate-fadeIn stagger-${i} ${
                i > 2 ? 'lg:col-span-2' : ''
              } ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}
            >
              <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                <div className="flex items-center gap-2">
                  <Skeleton className={`h-5 w-5 rounded ${darkMode ? 'bg-[#21262d]' : ''}`} />
                  <Skeleton className={`h-5 w-40 ${darkMode ? 'bg-[#21262d]' : ''}`} />
                </div>
                <Skeleton className={`h-4 w-56 mt-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
              </div>
              <div className="p-5 space-y-4">
                <Skeleton className={`h-20 w-full rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
                <Skeleton className={`h-20 w-full rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
                <Skeleton className={`h-10 w-full rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Settings
        </h1>
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Configure the autonomous intelligence engine
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scheduler Settings */}
        <SchedulerSettingsCard
          darkMode={darkMode}
          isEnabled={isEnabled}
          setIsEnabled={setIsEnabled}
          scheduleType={scheduleType}
          setScheduleType={setScheduleType}
          cronDayOfWeek={cronDayOfWeek}
          setCronDayOfWeek={setCronDayOfWeek}
          cronHour={cronHour}
          setCronHour={setCronHour}
          cronMinute={cronMinute}
          setCronMinute={setCronMinute}
          intervalHours={intervalHours}
          setIntervalHours={setIntervalHours}
          saving={saving}
          saveConfig={saveSettings}
        />

        {/* Status Card */}
        <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`} data-testid="scheduler-status">
          <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
            <h2 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Zap className="w-5 h-5 text-emerald-500" />
              Scheduler Status
            </h2>
          </div>
          <div className="p-5 space-y-5">
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-5">
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Status</span>
                <Badge 
                  className={isEnabled 
                    ? "bg-emerald-100 text-emerald-700 border-0" 
                    : "bg-gray-100 text-gray-500 border-0"
                  }
                >
                  {isEnabled ? "Active" : "Disabled"}
                </Badge>
              </div>
              
              <div className="space-y-4">
                <div className={`flex items-center justify-between py-3 border-b ${darkMode ? 'border-[#30363d]' : 'border-gray-200'}`}>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Last Run</span>
                  <span className={darkMode ? 'text-white' : 'text-gray-900'}>
                    {config?.last_run 
                      ? new Date(config.last_run).toLocaleString()
                      : "Never"
                    }
                  </span>
                </div>
                <div className={`flex items-center justify-between py-3 border-b ${darkMode ? 'border-[#30363d]' : 'border-gray-200'}`}>
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Interval</span>
                  <span className={darkMode ? 'text-white' : 'text-gray-900'}>{config?.interval_hours || 72} hours</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Next Run</span>
                  <span className={darkMode ? 'text-white' : 'text-gray-900'}>
                    {config?.last_run && isEnabled
                      ? new Date(new Date(config.last_run).getTime() + (config.interval_hours || 72) * 60 * 60 * 1000).toLocaleString()
                      : isEnabled ? "Pending first run" : "Disabled"
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`p-3 rounded-lg transition-colors cursor-pointer ${darkMode ? 'bg-[#21262d] hover:bg-[#30363d]' : 'bg-gray-50 hover:bg-gray-100'}`}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#DC143C]/10">
                    <RefreshCw className="w-4 h-4 text-[#DC143C]" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Manual Run</p>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>From Dashboard</p>
                  </div>
                </div>
              </div>
              <div className={`p-3 rounded-lg transition-colors cursor-pointer ${darkMode ? 'bg-[#21262d] hover:bg-[#30363d]' : 'bg-gray-50 hover:bg-gray-100'}`}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#D4AF37]/10">
                    <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>72-Hour Cycle</p>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Recommended</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Keys Configuration */}
        <ApiKeysCard
          darkMode={darkMode}
          apiKeysConfig={apiKeysConfig}
          useCustomKey={useCustomKey}
          setUseCustomKey={setUseCustomKey}
          openaiKey={openaiKey}
          setOpenaiKey={setOpenaiKey}
          showKey={showKey}
          setShowKey={setShowKey}
          savingKey={savingKey}
          saveApiKeys={saveApiKeys}
          deleteApiKey={deleteApiKey}
        />

        {/* About Section */}
        <div className={`rounded-xl overflow-hidden lg:col-span-2 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`} data-testid="about-section">
          <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
            <h2 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>About BlackSwan Intelligence Engine</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-[#DC143C]/10">
                    <Zap className="w-4 h-4 text-[#DC143C]" />
                  </div>
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Autonomous Monitoring</h4>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Continuously monitors competitor websites and social media for changes and opportunities.
                </p>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <Sparkles className="w-4 h-4 text-emerald-500" />
                  </div>
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>AI Content Generation</h4>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Powered by GPT-5.2, generates optimized posts with multiple hook variants.
                </p>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-[#D4AF37]/10">
                    <SettingsIcon className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Video Production</h4>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Creates professional short videos using Sora 2 AI for vertical formats.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
